import {
  BulkWalletEntryMode,
  OrderType,
  ProductCode,
  UserLevelType,
} from "~/common/constants"
import { getUserLevelCode } from "~/pages/Staff/useStaffUserLookup"
import { levelLabel, productCodeLabel } from "~/pages/Staff/labels"

export type StaffOrderFormValues = {
  externalPaymentNo: string
  mobile: string
  orderType: OrderType
  paymentMethod: API.Staff.CreateStaffOrderOpLogParams["payment_method"]
  walletRechargeAmount?: number | null
  purchaseUnitPrice?: number | null
  refundUnitPrice?: number | null
  bulkEntryMode?: BulkWalletEntryMode
  rechargeAmountYuan?: string
}

export const BULK_WALLET_LIMITS = {
  quantity: { min: 1, max: 100000 },
  purchaseUnitPrice: { min: 22, max: 30 },
  refundUnitPrice: { min: 20, max: 30 },
} as const

const isIntegerInRange = (
  value: number | null | undefined,
  range: { min: number; max: number },
): value is number =>
  typeof value === "number" &&
  Number.isInteger(value) &&
  value >= range.min &&
  value <= range.max

const getWalletProductCode = (user?: API.Staff.StaffUser | null) =>
  getUserLevelCode(user) === UserLevelType.pro
    ? ProductCode.walletProV1
    : ProductCode.walletFreeV1

export const getStaffOrderProductCode = (
  orderType: OrderType,
  user?: API.Staff.StaffUser | null,
) => {
  if (orderType === OrderType.bulkWalletPackage) return ProductCode.bulkWalletV1
  if (orderType === OrderType.levelPackage) return ProductCode.proMonthlyV1
  return getWalletProductCode(user)
}

export const getStaffProductHelpText = (
  orderType: OrderType,
  user?: API.Staff.StaffUser | null,
) => {
  if (orderType === OrderType.bulkWalletPackage)
    return "按线下成交约定选择按积点数量或充值金额录入，适用于免费版和专业版账号，不改变会员等级。"
  if (orderType === OrderType.levelPackage)
    return "会员等级套餐固定录入专业版月度套餐，不需要填写积点数量。"
  return `当前用户等级为 ${levelLabel[getUserLevelCode(user)]}，积点钱包套餐自动录入${productCodeLabel[getWalletProductCode(user)]}。`
}

export const getBulkWalletAmount = (
  quantity?: number | null,
  purchaseUnitPrice?: number | null,
): number | undefined =>
  isIntegerInRange(quantity, BULK_WALLET_LIMITS.quantity) &&
  isIntegerInRange(purchaseUnitPrice, BULK_WALLET_LIMITS.purchaseUnitPrice)
    ? quantity * purchaseUnitPrice
    : undefined

export const parseBulkWalletAmount = (value?: string): number | undefined => {
  if (typeof value !== "string" || !/^\d+(?:\.\d{1,2})?$/.test(value.trim()))
    return undefined
  const [yuan, fraction = ""] = value.trim().split(".")
  const cents = Number(yuan) * 100 + Number(fraction.padEnd(2, "0"))
  return Number.isSafeInteger(cents) && cents > 0 ? cents : undefined
}

export const getBulkWalletQuantity = (
  amount?: number | null,
  purchaseUnitPrice?: number | null,
): number | undefined => {
  if (
    !Number.isSafeInteger(amount) ||
    !amount ||
    amount <= 0 ||
    !isIntegerInRange(purchaseUnitPrice, BULK_WALLET_LIMITS.purchaseUnitPrice)
  )
    return undefined
  const quantity = Math.floor(
    (amount + purchaseUnitPrice - 1) / purchaseUnitPrice,
  )
  return isIntegerInRange(quantity, BULK_WALLET_LIMITS.quantity)
    ? quantity
    : undefined
}

export const getStaffBulkWalletAmount = (record: API.Staff.StaffOrderOpLog) =>
  record.amount ??
  (record.bulk_entry_mode === BulkWalletEntryMode.amount
    ? undefined
    : getBulkWalletAmount(
        record.wallet_recharge_amount,
        record.purchase_unit_price,
      ))

export const buildStaffOrderPayload = (
  values: StaffOrderFormValues,
  user: API.Staff.StaffUser | null,
): API.Staff.CreateStaffOrderOpLogParams => {
  if (!user?.id || values.mobile.trim() !== user.mobile)
    throw new Error("请先查询并确认被充值用户")
  const externalPaymentNo = values.externalPaymentNo.trim()
  if (!externalPaymentNo) throw new Error("请输入外部支付渠道交易号")
  const base = {
    user_id: user.id,
    payment_method: values.paymentMethod,
    external_payment_no: externalPaymentNo,
  }
  if (values.orderType === OrderType.bulkWalletPackage) {
    const { walletRechargeAmount, purchaseUnitPrice, refundUnitPrice } = values
    if (
      !isIntegerInRange(purchaseUnitPrice, BULK_WALLET_LIMITS.purchaseUnitPrice)
    )
      throw new Error("购买单价必须为 22～30 的整数分")
    if (!isIntegerInRange(refundUnitPrice, BULK_WALLET_LIMITS.refundUnitPrice))
      throw new Error("退款结算单价必须为 20～30 的整数分")
    if (refundUnitPrice < purchaseUnitPrice)
      throw new Error("退款结算单价不可低于购买单价")
    const agreement = {
      ...base,
      order_type: OrderType.bulkWalletPackage as const,
      product_code: ProductCode.bulkWalletV1 as const,
      purchase_unit_price: purchaseUnitPrice,
      refund_unit_price: refundUnitPrice,
    }
    const mode = values.bulkEntryMode ?? BulkWalletEntryMode.quantity
    if (mode === BulkWalletEntryMode.amount) {
      const amount = parseBulkWalletAmount(values.rechargeAmountYuan)
      if (!amount) throw new Error("充值金额须大于 0，且最多两位小数")
      if (!getBulkWalletQuantity(amount, purchaseUnitPrice))
        throw new Error("到账积点数量不能超过 100000")
      return { ...agreement, bulk_entry_mode: mode, amount }
    }
    if (mode !== BulkWalletEntryMode.quantity) throw new Error("请选择录入方式")
    if (!isIntegerInRange(walletRechargeAmount, BULK_WALLET_LIMITS.quantity))
      throw new Error("购买数量必须为 1～100000 的整数")
    return {
      ...agreement,
      bulk_entry_mode: mode,
      wallet_recharge_amount: walletRechargeAmount,
    }
  }
  if (values.orderType === OrderType.levelPackage)
    return {
      ...base,
      order_type: values.orderType,
      product_code: ProductCode.proMonthlyV1,
    }
  if (!values.walletRechargeAmount) throw new Error("请选择积点数量")
  return {
    ...base,
    order_type: OrderType.walletPackage,
    product_code: getWalletProductCode(user),
    wallet_recharge_amount: values.walletRechargeAmount,
  }
}
